import { createContext, useContext, useState, useCallback, useEffect } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser]               = useState(null);
  const [initialized, setInitialized] = useState(false);

  // On mount: restore user from token so page refresh doesn't log out
  useEffect(() => {
    const token = localStorage.getItem("sc_access_token");
    if (!token) {
      setInitialized(true);
      return;
    }
    api.get("/auth/me")
      .then(({ data }) => setUser(data))
      .catch(() => {
        // Token invalid / expired and refresh failed — clear everything
        localStorage.removeItem("sc_access_token");
        sessionStorage.removeItem("sc_view");
      })
      .finally(() => setInitialized(true));
  }, []);

  const _persist = (accessToken, userData) => {
    localStorage.setItem("sc_access_token", accessToken);
    setUser(userData);
  };

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    _persist(data.access_token, data.user);
    return data.user;
  }, []);

  const signup = useCallback(async ({ name, email, password, role }) => {
    const { data } = await api.post("/auth/signup", { name, email, password, role });
    _persist(data.access_token, data.user);
    return data.user;
  }, []);

  const socialLogin = useCallback(async ({ provider, provider_id, email, name, role = "patient" }) => {
    const { data } = await api.post("/auth/social-login", { provider, provider_id, email, name, role });
    _persist(data.access_token, data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("sc_access_token");
    sessionStorage.removeItem("sc_view");
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, initialized, login, signup, socialLogin, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
